import { Link } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import SiteLayout from '@/components/feature/SiteLayout';
import ArticleCard, { toneBadge } from '@/components/feature/ArticleCard';
import SubscribeForm from '@/components/feature/SubscribeForm';
import { SITE } from '@/data/site';
import { categories } from '@/data/categories';
import { articles, getEditorPicks, getFeatured, getLatest } from '@/data/articles';

export default function Home() {
  const featured = getFeatured();
  const hero = featured[0] ?? articles[0];
  const latest = getLatest(6);
  const picks = getEditorPicks().slice(0, 3);
  const hot = articles.slice(0, 5);
  const heroCategory = categories.find((c) => c.slug === hero.category);
  const allTags = Array.from(new Set(articles.flatMap((a) => a.tags))).slice(0, 12);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE.name,
      alternateName: SITE.subName,
      url: SITE.domain,
      description: SITE.description,
      email: SITE.email,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE.name,
      url: SITE.domain,
      description: SITE.description,
      inLanguage: 'zh-CN',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE.domain}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${SITE.name} - 娱乐热点、热剧综与网络热梗观察站`,
      url: SITE.domain,
      isPartOf: { '@type': 'WebSite', url: SITE.domain },
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${SITE.name} - 娱乐热点、热剧综与网络热梗观察站`}
        description={SITE.description}
        path="/"
        schema={schema}
      />

      {/* 首屏 */}
      <section className="max-w-container mx-auto px-4 md:px-8 pt-10 md:pt-14">
        <div className="max-w-3xl animate-fade-up">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.3em] text-primary-600 font-label">
            <span className="w-6 h-px bg-primary-400" />
            {SITE.subName}
          </span>
          <h1 className="mt-4 font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight tracking-tight">
            把热闹，看成门道
          </h1>
          <p className="mt-5 text-base md:text-lg leading-8 text-foreground-600">
            娱乐话题每天都在换，但热闹背后的逻辑其实有迹可循。这里用可读的长内容，
            把热搜、剧综和网络热梗拆开来讲清楚——畅读全文，免费无限制浏览。
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to="/category/yule-redian"
              className="h-11 px-6 inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-fire-line" />
              </span>
              从娱乐热点开始读
            </Link>
            <Link
              to="/about"
              className="h-11 px-6 inline-flex items-center rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              了解我们是谁
            </Link>
          </div>
        </div>

        {/* 重点推荐 + 热读榜 */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <article className="lg:col-span-8 group relative overflow-hidden rounded-lg h-[360px] md:h-[440px]">
            <img
              src={hero.cover}
              alt={hero.title}
              width={1200}
              height={800}
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2.5 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                  重点推荐
                </span>
                {heroCategory && (
                  <span className="px-2.5 py-0.5 rounded-full bg-background-50/20 text-background-50 text-xs backdrop-blur">
                    {heroCategory.name}
                  </span>
                )}
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 leading-snug max-w-2xl">
                <Link to={`/article/${hero.slug}`} className="hover:text-primary-200 transition-colors">
                  {hero.title}
                </Link>
              </h2>
              <p className="mt-3 text-sm md:text-base leading-7 text-background-100/85 max-w-2xl line-clamp-2">
                {hero.excerpt}
              </p>
              <div className="mt-4 flex items-center gap-4 text-xs text-background-100/70">
                <span>{hero.author}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" /> {hero.readingTime} 分钟
                </span>
                <time dateTime={hero.publishedAt}>{hero.publishedAt}</time>
              </div>
            </div>
          </article>

          <aside className="lg:col-span-4 bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-fire-fill" />
                </span>
                热读榜
              </h2>
              <Link
                to="/search"
                className="text-xs text-foreground-500 hover:text-primary-600 transition-colors"
              >
                全部内容
              </Link>
            </div>
            <div>
              {hot.map((a, i) => (
                <ArticleCard key={a.slug} article={a} variant="compact" index={i} />
              ))}
            </div>
          </aside>
        </div>
      </section>

      {/* 栏目导航 */}
      <section className="max-w-container mx-auto px-4 md:px-8 mt-16 md:mt-20">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              按栏目逛逛
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              四个方向，覆盖娱乐话题的不同侧面
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group flex flex-col p-5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
            >
              <span
                className={`w-11 h-11 rounded-md flex items-center justify-center ${toneBadge[c.accent]}`}
              >
                <i className={`${c.icon} text-2xl`} />
              </span>
              <span className="mt-4 font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                {c.name}
              </span>
              <span className="mt-1.5 text-xs text-foreground-500">{c.tagline}</span>
              <span className="mt-4 flex items-center gap-1 text-xs text-primary-600">
                进入栏目
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新发布 + 标签 */}
      <section className="max-w-container mx-auto px-4 md:px-8 mt-16 md:mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                最新发布
              </h2>
              <span className="text-xs text-foreground-500">
                持续更新 · {articles.length} 篇长文
              </span>
            </div>
            <div>
              {latest.map((a) => (
                <ArticleCard key={a.slug} article={a} variant="list" />
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-8">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="font-heading font-semibold text-foreground-950 mb-3">
                  大家在看什么
                </h3>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                <span className="w-9 h-9 rounded-md bg-accent-500 text-accent-50 flex items-center justify-center">
                  <i className="ri-compass-3-line text-xl" />
                </span>
                <h3 className="mt-3 font-heading font-semibold text-accent-900">
                  第一次来？先看这两篇
                </h3>
                <ul className="mt-3 space-y-3">
                  {articles.slice(10, 12).map((a) => (
                    <li key={a.slug}>
                      <Link
                        to={`/article/${a.slug}`}
                        className="text-sm text-accent-900 hover:text-primary-600 transition-colors leading-6 block"
                      >
                        {a.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* 编辑精选 */}
      <section className="max-w-container mx-auto px-4 md:px-8 mt-16 md:mt-20">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-1.5 h-6 rounded-full bg-primary-500" />
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            编辑精选
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {picks.map((a) => (
            <ArticleCard key={a.slug} article={a} />
          ))}
        </div>
      </section>

      {/* 订阅 */}
      <section className="max-w-container mx-auto px-4 md:px-8 mt-16 md:mt-20">
        <div className="rounded-lg bg-accent-950 px-6 py-10 md:px-12 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 leading-snug">
                不想错过热门话题？
              </h2>
              <p className="mt-3 text-sm md:text-base leading-7 text-background-200/80 max-w-md">
                留下邮箱，我们会在每期新内容上线时发一封简短的提醒。没有广告，只有值得读的那几篇。
              </p>
            </div>
            <div>
              <SubscribeForm />
              <p className="mt-3 text-xs text-background-200/60">
                我们只发送本站内容更新提醒，随时可以取消。
              </p>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}